import { ref, computed, toValue, watch, onMounted, onUnmounted } from 'vue';
import { useGraphModelStorage } from '@/composables/useGraphModelStorage';
import { useProForecastModel } from '@/composables/useProForecastModel';

const NWS_MARINE_MODEL_ID = -2;
const BLEND_MODEL_ID = -1;

// Model ids must be numbers to match the API's numeric model_id (favorites and
// legacy storage can supply them as strings, which breaks selection matching).
const toNumericIds = (ids) =>
  (Array.isArray(ids) ? ids : []).map(Number).filter((id) => !Number.isNaN(id));

// Orchestrates model selection for the wind graphs: per-graph-type persistence,
// the Pro Forecast injection, default selection, and the EventBus contract the
// legacy graphs (Graph.js / CurrentConditions.js) listen on. The presentational
// ModelSelector stays a leaf component; this owns the policy around it.
//
//   spot:    getter/ref/object for the spot's API data (for the Pro Forecast model)
//   spotId:  getter/ref for the resolved spot id (persistence + event scoping)
//   variant: Ref<'basic'|'detailed'> — the active graph type
//   storeKey: unique store key (favorites use a per-instance key)
//   initialSelection: model ids to seed from (favorites restore); else null
export const useWindGraphModelSelection = ({ spot, spotId, variant, storeKey = null, initialSelection = null }) => {
  // Basic and detailed graphs keep separate remembered model sets under their
  // own storage keys.
  const basicStorage = useGraphModelStorage('WindGraphSettings');
  const detailedStorage = useGraphModelStorage('DetailedWindGraphSettings');
  const storageFor = (type) => (type === 'detailed' ? detailedStorage : basicStorage);

  const { selectorOption: proForecastSelectorOption } = useProForecastModel(spot, storeKey);

  const resolvedSpotId = computed(() => toValue(spotId));


  // Pro Forecast is only offered on the basic graph.
  const additionalModels = computed(() =>
    variant.value === 'basic' && proForecastSelectorOption.value ? [proForecastSelectorOption.value] : []
  );

  // The detailed graph requests the NWS Marine forecast model.
  const includeNWSMarineForecast = computed(() => variant.value === 'detailed');

  function readSavedModels(type) {
    const saved = storageFor(type).read(resolvedSpotId.value);
    return Array.isArray(saved) ? saved : [];
  }

  // Seed from a favorites restore (initial) or the active graph type's saved set.
  const seededModels = toNumericIds(initialSelection != null ? initialSelection : readSavedModels(variant.value));
  const selectedModelIds = ref(seededModels);
  // A non-empty seed came from a saved/favorited choice; an empty one will default.
  let selectionIsUserChosen = selectedModelIds.value.length > 0;

  // The model list reported by ModelSelector, used to compute default selections.
  const availableModels = ref([]);
  // Models the graph has rejected (no data / upgrade required). Excluded from
  // default computation so a later recompute can't re-add a rejected model.
  const unavailableModelIds = ref(new Set());

  const modelsForDefault = () =>
    availableModels.value.filter((model) => !unavailableModelIds.value.has(model.model_id));

  function isAccessible(model) {
    return !model.upgrade_available;
  }

  // The NWS Marine model (id -2) is added to the list via include_nws_marine_forecast.
  function isNWSMarineModel(model) {
    return model.model_id === NWS_MARINE_MODEL_ID;
  }

  // Default selection for a graph type when nothing is saved: free users get the
  // Blend (-1) model, others the first accessible model; the detailed graph also
  // includes the NWS Marine forecast as a second default.
  function defaultModelIds(type, models) {
    if (!models.length) return [];

    const hasPremiumAccess = models.some((model) => model.is_premium && isAccessible(model));
    const blend = models.find((model) => model.model_id === BLEND_MODEL_ID);
    const firstAccessible = models.find(isAccessible);
    const primary = hasPremiumAccess ? firstAccessible : (blend || firstAccessible);

    const ids = primary ? [primary.model_id] : [];

    if (type === 'detailed') {
      const nws = models.find(isNWSMarineModel);
      if (nws && !ids.includes(nws.model_id)) ids.push(nws.model_id);
    }

    return ids;
  }

  function sameIds(a, b) {
    return a.length === b.length && a.every((id) => b.includes(id));
  }

  // Set the selection and notify the graph. Only deliberate user choices are
  // persisted; defaults are recomputed each load so they stay current.
  function setSelection(rawIds, { userChosen }) {
    const ids = toNumericIds(rawIds);
    selectionIsUserChosen = userChosen;
    selectedModelIds.value = ids;

    if (userChosen) storageFor(variant.value).save(resolvedSpotId.value, ids);

    window.$EventBus.dispatchEvent('jw-wind-graph-models-selected', {
      spotId: resolvedSpotId.value,
      selectedModels: ids,
    });
  }

  // Apply the default selection for the active graph type unless the user has
  // chosen their own. Re-applies when the default changes (e.g. NWS Marine
  // becomes available switching to the detailed graph).
  function applyDefaultSelection() {
    if (selectionIsUserChosen || !availableModels.value.length) return;

    const ids = defaultModelIds(variant.value, modelsForDefault());
    if (!ids.length || sameIds(selectedModelIds.value, ids)) return;

    setSelection(ids, { userChosen: false });
  }

  function onModelsLoaded(models) {
    availableModels.value = models || [];

    // The initial seed runs in setup() before the spot id is ready, so the
    // saved selection couldn't be read yet. Restore it here (the spot id is
    // available once the list has been fetched) before falling back to default.
    if (!selectionIsUserChosen) {
      const saved = readSavedModels(variant.value).filter((id) => !unavailableModelIds.value.has(id));

      if (saved.length) {
        setSelection(saved, { userChosen: true });
        return;
      }
    }

    applyDefaultSelection();
  }

  // The Vue ModelSelector changed: persist the selection and notify the graph
  // (Graph.js listens and runs _modelsChangedCallback to update in place).
  function onModelsSelected(modelIds) {
    setSelection(Array.isArray(modelIds) ? modelIds : [], { userChosen: true });
  }

  // The graph rejected a model (no data / upgrade required); keep its selector
  // item visible, mark it unavailable, and uncheck it.
  function handleModelUnavailable(e) {
    console.log("🚀 ~ useWindGraphModelSelection.js ~ handleModelUnavailable ~ e:", e)
    const { spotId } = e.detail;
    const modelId = Number(e.detail.modelId);

    if (spotId !== resolvedSpotId.value) return;

    // Remember the rejection so default computation won't re-add this model.
    unavailableModelIds.value.add(modelId);

    const remaining = selectedModelIds.value.filter((id) => id !== modelId);

    // Models remain: just sync the UI (the graph already dropped the model).
    if (remaining.length) {
      selectedModelIds.value = remaining;
      return;
    }

    // Nothing left selected — fall back to the default (excluding rejected
    // models) and tell the graph to render it, rather than leaving it empty.
    const ids = defaultModelIds(variant.value, modelsForDefault());

    if (ids.length) {
      setSelection(ids, { userChosen: false });
    } else {
      selectedModelIds.value = remaining;
    }
  }

  // Answer a graph's request to (re)broadcast the current selection. A graph's
  // own data fetch can finish after the model list's, so it may register its
  // 'jw-wind-graph-models-selected' listener after the initial broadcast and
  // miss it (the intermittent "models don't show on load" bug). Each graph
  // dispatches this on init; we reply with the current selection so it renders
  // regardless of which fetch won the race.
  function handleModelsRequested(e) {
    const { spotId } = e.detail || {};
    if (spotId !== resolvedSpotId.value) return;

    window.$EventBus.dispatchEvent('jw-wind-graph-models-selected', {
      spotId: resolvedSpotId.value,
      selectedModels: selectedModelIds.value,
    });
  }

  // Registered synchronously (not in onMounted) so the listener exists before
  // the child graph components mount: nowcast graphs draw synchronously during
  // their own mount, which runs before this composable's parent onMounted would.
  window.$EventBus.addEventListener('jw-wind-graph-request-models', handleModelsRequested);

  // Switching graph type swaps to that type's separately-remembered selection.
  // Flipping includeNWSMarineForecast re-fetches the model list, so the default
  // (if this type has nothing saved) is applied in onModelsLoaded against the
  // correct list rather than here against the previous one.
  watch(variant, (type) => {
    // Availability is per graph type (the model lists differ), so start fresh.
    unavailableModelIds.value.clear();

    const saved = readSavedModels(type);
    selectedModelIds.value = saved;
    selectionIsUserChosen = saved.length > 0;
  });

  onMounted(() => {
    window.$EventBus.addEventListener('jw-wind-graph-model-unavailable', handleModelUnavailable);
  });

  onUnmounted(() => {
    window.$EventBus.removeEventListener('jw-wind-graph-model-unavailable', handleModelUnavailable);
    window.$EventBus.removeEventListener('jw-wind-graph-request-models', handleModelsRequested);
  });

  return {
    selectedModelIds,
    unavailableModelIds,
    includeNWSMarineForecast,
    additionalModels,
    onModelsLoaded,
    onModelsSelected,
  };
};
